import React from 'react';

import 'Css/device_qr_code.css';

const decodeDeviceId = (encodedDeviceId) => {
  try {
    const normalized = (encodedDeviceId || '').replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    return atob(padded);
  } catch (e) {
    return '';
  }
};

class DeviceAccessKeypad extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      pressedKey: null,
      typedPreview: '',
      keypadMessage: 'DIGITA CODICE',
      isSubmitting: false,
      submitStatus: null
    };

    this.config = require('../../../../config/config.json');
    this.pressTimeout = null;
    this.messageTimeout = null;
  }

  componentWillUnmount() {
    if (this.pressTimeout) {
      clearTimeout(this.pressTimeout);
    }

    if (this.messageTimeout) {
      clearTimeout(this.messageTimeout);
    }
  }

  getSearchParams() {
    return new URLSearchParams(window.location.search);
  }

  getDeviceToken() {
    const searchParams = this.getSearchParams();
    const encodedDeviceId = searchParams.get('device');

    if (encodedDeviceId) {
      return encodedDeviceId;
    }

    // Fallback per eventuali vecchi QR già generati con id in chiaro.
    const legacyDeviceId = searchParams.get('id') || '';
    try {
      return btoa(legacyDeviceId)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/g, '');
    } catch (e) {
      return '';
    }
  }

  getDeviceId() {
    const deviceToken = this.getDeviceToken();
    return decodeDeviceId(deviceToken);
  }

  getDeviceName() {
    const searchParams = this.getSearchParams();
    return searchParams.get('name') || 'Dispositivo';
  }

  resetTemporaryMessage(delay = 850) {
    if (this.messageTimeout) {
      clearTimeout(this.messageTimeout);
    }

    this.messageTimeout = setTimeout(() => {
      this.setState({ keypadMessage: 'DIGITA CODICE', submitStatus: null });
    }, delay);
  }

  async submitPin() {
    const pin = this.state.typedPreview;

    if (!pin || this.state.isSubmitting) {
      this.setState({
        typedPreview: '',
        keypadMessage: pin ? 'ATTENDI' : 'NESSUN CODICE',
        submitStatus: pin ? 'loading' : 'error'
      });
      this.resetTemporaryMessage(pin ? 700 : 650);
      return;
    }

    const deviceToken = this.getDeviceToken();

    if (!deviceToken) {
      this.setState({
        typedPreview: '',
        keypadMessage: 'QR NON VALIDO',
        submitStatus: 'error'
      });
      this.resetTemporaryMessage(1200);
      return;
    }

    this.setState({
      isSubmitting: true,
      keypadMessage: 'INVIO PIN',
      submitStatus: 'loading'
    });

    try {
      const response = await fetch(`${this.config.controlPointAPI}/public_pin_open`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          device: deviceToken,
          pin
        })
      });

      let result = null;
      try {
        result = await response.json();
      } catch (e) {
        result = null;
      }

      if (response.ok && result && result.authorized) {
        this.setState({
          typedPreview: '',
          isSubmitting: false,
          keypadMessage: 'ACCESSO OK',
          submitStatus: 'success'
        });
        this.resetTemporaryMessage(1300);
        return;
      }

      this.setState({
        typedPreview: '',
        isSubmitting: false,
        keypadMessage: 'PIN ERRATO',
        submitStatus: 'error'
      });
      this.resetTemporaryMessage(1300);
    } catch (e) {
      this.setState({
        typedPreview: '',
        isSubmitting: false,
        keypadMessage: 'ERRORE RETE',
        submitStatus: 'error'
      });
      this.resetTemporaryMessage(1300);
    }
  }

  clearPin() {
    if (this.state.isSubmitting) {
      return;
    }

    this.setState({
      typedPreview: '',
      keypadMessage: 'CANCELLATO',
      submitStatus: null
    });

    this.resetTemporaryMessage(650);
  }

  onKeyPress(key) {
    if (this.state.isSubmitting) {
      return;
    }

    if (this.pressTimeout) {
      clearTimeout(this.pressTimeout);
    }

    this.setState({ pressedKey: key });

    if (key === '*') {
      this.clearPin();
    } else if (key === '#') {
      this.submitPin();
    } else {
      this.setState((prevState) => ({
        typedPreview: `${prevState.typedPreview}${key}`.slice(-8),
        keypadMessage: 'DIGITA CODICE',
        submitStatus: null
      }));
    }

    this.pressTimeout = setTimeout(() => {
      this.setState({ pressedKey: null });
    }, 220);
  }

  getKeyAriaLabel(key) {
    if (key === '*') {
      return 'Cancella codice';
    }

    if (key === '#') {
      return 'Invia codice';
    }

    return `Tasto ${key}`;
  }

  renderKey(key) {
    const isPressed = this.state.pressedKey === key;

    return (
      <button
        type="button"
        className={`virtual-keypad-key ${isPressed ? 'pressed' : ''}`}
        onClick={() => this.onKeyPress(key)}
        aria-label={this.getKeyAriaLabel(key)}
        disabled={this.state.isSubmitting}
      >
        <span>{key}</span>
      </button>
    );
  }

  render() {
    this.getDeviceId(); // Mantiene disponibile l'id internamente senza mostrarlo all'utente.
    const deviceName = this.getDeviceName();
    const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];
    const displayClassName = `virtual-keypad-display large ${this.state.submitStatus || ''}`;

    return (
      <div id="deviceAccessKeypadPage">
        <div className="public-keypad-wrapper edge-to-edge">
          <div className="public-keypad-header simple">
            <strong>{deviceName}</strong>
          </div>

          <div className="virtual-keypad-shell full-screen">
            <div className="virtual-keypad-device large">
              <div className={displayClassName}>
                <span>{this.state.typedPreview ? '•'.repeat(this.state.typedPreview.length) : this.state.keypadMessage}</span>
              </div>

              <div className="virtual-keypad-grid large">
                {keys.map((key) => this.renderKey(key))}
              </div>

              <div className="virtual-keypad-brand">DomoDroid</div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default DeviceAccessKeypad;
export { DeviceAccessKeypad };
